import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fechaParaMirar } from '../lib/fecha';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';

interface Lista {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly monedaCodigo: string;
  readonly expresion: 'neto' | 'final';
  readonly esPredeterminada: boolean;
}

interface FilaDePrecio {
  readonly varianteId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly talle: string | null;
  readonly color: string | null;
  readonly esUnica: boolean;
  readonly unidad: string;
  readonly precio: string | null;
  readonly vigenteDesde: string | null;
  /** Lo que saldría reponerlo hoy, en su moneda. Es la base del recálculo de E20. */
  readonly costoDeReposicion: string | null;
  readonly monedaDelCosto: string | null;
  readonly costoVigenteDesde: string | null;
}

interface LineaDeHistorial {
  readonly id: string;
  readonly precio: string;
  readonly precioAnterior: string | null;
  readonly motivo: string;
  readonly vigenteDesde: string;
  readonly correoDelUsuario: string | null;
}

/** `aumento_de_lista` → `precios.motivoAumentoDeLista`. */
function claveDeMotivo(motivo: string): string {
  const camello = motivo.replace(/_(.)/g, (_, letra: string) => letra.toUpperCase());
  return `precios.motivo${camello.charAt(0).toUpperCase()}${camello.slice(1)}`;
}

/**
 * Cómo se muestra un precio guardado dentro del campo editable.
 *
 * La base guarda cuatro decimales porque es la escala de la aritmética exacta, pero cuatro
 * decimales en pantalla son precisión interna, no información: nadie carga $25.000,0000. Se
 * esconden los dos últimos **sólo cuando son cero**; si alguien cargó un precio con cuatro
 * decimales de verdad —pasa en mayorista, con precios por unidad de un bulto— se ven los
 * cuatro, porque esconderlos sería mostrar un número que no es el que está guardado.
 */
function paraEditar(precio: string): string {
  return /\.\d{2}00$/.test(precio) ? precio.slice(0, -2) : precio;
}

/** Sólo se manda lo que cambió, y sin ceros a la izquierda ni espacios. */
function normalizar(valor: string): string {
  return valor.trim().replace(',', '.');
}

/**
 * Precios: cargar, corregir y aumentar.
 *
 * Tres decisiones que se ven al usarla.
 *
 * **La lista muestra también lo que no tiene precio.** La pregunta real del comercio no es
 * "¿qué precios cargué?" sino "¿qué me falta cargar?", y una pantalla que sólo muestra lo
 * cargado nunca la responde.
 *
 * **Se edita toda la grilla y se guarda una vez.** Cargar precios es escribir muchos
 * números seguidos; un diálogo por artículo convierte media hora de trabajo en media tarde.
 * Lo que se manda es sólo lo que cambió.
 *
 * **El aumento de lista está a un botón.** Sin él, un aumento del proveedor obliga a tocar
 * miles de precios a mano, y lo que pasa en la práctica es que no se tocan y se vende a
 * pérdida.
 */
export function Precios(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeEditar = usePermiso('PRECIOS.PRECIO.EDITAR');
  const puedeAumentar = usePermiso('PRECIOS.AUMENTO.APLICAR');

  const [listaId, setListaId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [editados, setEditados] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [aumentoAbierto, setAumentoAbierto] = useState(false);
  const [porcentaje, setPorcentaje] = useState('');
  const [decimales, setDecimales] = useState('2');

  const [historialDe, setHistorialDe] = useState<FilaDePrecio | null>(null);
  // Programar un precio para una fecha (E36): el de hoy no se toca hasta ese día.
  const [programando, setProgramando] = useState<FilaDePrecio | null>(null);
  const [programa, setPrograma] = useState({ precio: '', fecha: '' });
  const [errorDeProgramar, setErrorDeProgramar] = useState<string | null>(null);

  const [costoDe, setCostoDe] = useState<FilaDePrecio | null>(null);
  const [costoEditado, setCostoEditado] = useState('');
  const [monedaDelCosto, setMonedaDelCosto] = useState('');

  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    queryFn: () => pedir<Lista[]>('/listas-de-precios'),
  });

  const monedas = useQuery({
    queryKey: ['monedas'],
    queryFn: () => pedir<{ monedaCodigo: string; esBase: boolean }[]>('/monedas'),
  });

  const listaActiva =
    listas.data?.find((lista) => lista.id === listaId) ??
    listas.data?.find((lista) => lista.esPredeterminada) ??
    listas.data?.[0];
  const listaActivaId = listaActiva?.id ?? '';

  const consulta = useQuery({
    queryKey: ['precios', listaActivaId, busqueda],
    enabled: listaActivaId !== '',
    queryFn: () =>
      pedir<FilaDePrecio[]>(
        `/listas-de-precios/${listaActivaId}/precios${
          busqueda.trim() === '' ? '' : `?texto=${encodeURIComponent(busqueda.trim())}`
        }`,
      ),
  });

  const historial = useQuery({
    queryKey: ['historial-de-precio', listaActivaId, historialDe?.varianteId],
    enabled: historialDe !== null,
    queryFn: () =>
      pedir<LineaDeHistorial[]>(
        `/listas-de-precios/${listaActivaId}/precios/${historialDe?.varianteId ?? ''}/historial`,
      ),
  });

  // Sólo lo que cambió de verdad: reenviar lo mismo ensuciaría el historial.
  const cambios = useMemo(() => {
    const filas = consulta.data ?? [];
    return filas
      .filter((fila) => {
        const editado = editados[fila.varianteId];
        if (editado === undefined) return false;
        const limpio = normalizar(editado);
        if (limpio === '') return false;
        return limpio !== (fila.precio === null ? '' : normalizar(fila.precio));
      })
      .map((fila) => ({
        varianteId: fila.varianteId,
        precio: normalizar(editados[fila.varianteId] ?? ''),
      }));
  }, [consulta.data, editados]);

  const sinPrecio = (consulta.data ?? []).filter((fila) => fila.precio === null).length;

  const guardar = useMutation({
    mutationFn: () =>
      pedir<{ cargados: number }>(`/listas-de-precios/${listaActivaId}/precios`, {
        method: 'POST',
        cuerpo: { precios: cambios, motivo: 'carga' },
      }),
    onSuccess: async (resultado) => {
      setEditados({});
      setError(null);
      setAviso(t('precios.guardados', { count: resultado.cargados }));
      await clienteDeConsultas.invalidateQueries({ queryKey: ['precios'] });
    },
    onError: (fallo: unknown) => {
      setAviso(null);
      setError(textoDeError(fallo));
    },
  });

  const aumentar = useMutation({
    mutationFn: () =>
      pedir<{ actualizados: number }>(`/listas-de-precios/${listaActivaId}/aumento`, {
        method: 'POST',
        cuerpo: {
          porcentaje: normalizar(porcentaje),
          decimalesDeRedondeo: Number(decimales),
        },
      }),
    onSuccess: async (resultado) => {
      setAumentoAbierto(false);
      setPorcentaje('');
      setError(null);
      setAviso(t('precios.aumentados', { count: resultado.actualizados }));
      await clienteDeConsultas.invalidateQueries({ queryKey: ['precios'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const guardarCosto = useMutation({
    mutationFn: () =>
      pedir<void>(`/variantes/${costoDe?.varianteId ?? ''}/costo-de-reposicion`, {
        method: 'POST',
        cuerpo: { costo: normalizar(costoEditado), monedaCodigo: monedaDelCosto },
      }),
    onSuccess: async () => {
      setCostoDe(null);
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['precios'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const conPrecio = (consulta.data ?? []).filter((fila) => fila.precio !== null).length;

  const programados = useQuery({
    queryKey: ['precios-programados', listaActivaId],
    enabled: listaActivaId !== '',
    queryFn: () =>
      pedir<
        {
          id: string;
          codigo: string;
          nombre: string;
          precio: string;
          precioDeHoy: string | null;
          vigenteDesde: string;
        }[]
      >(`/precios-programados?listaId=${listaActivaId}`),
  });

  const programar = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>(`/listas-de-precios/${listaActivaId}/programados`, {
        method: 'POST',
        cuerpo: {
          varianteId: programando?.varianteId ?? '',
          precio: programa.precio.trim(),
          vigenteDesde: programa.fecha,
        },
      }),
    onSuccess: async () => {
      setProgramando(null);
      setErrorDeProgramar(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['precios-programados'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeProgramar(textoDeError(fallo));
    },
  });

  const cancelarProgramado = useMutation({
    mutationFn: (id: string) => pedir<void>(`/precios-programados/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: ['precios-programados'] });
    },
  });

  const columnas: Columna<FilaDePrecio>[] = [
    {
      clave: 'codigo',
      titulo: t('precios.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    {
      clave: 'nombre',
      titulo: t('precios.articulo'),
      contenido: (fila) => (
        <>
          {fila.nombre}
          {fila.esUnica ? null : (
            <span className="block text-xs text-texto-tenue">
              {[fila.talle, fila.color].filter((parte) => parte !== null).join(' · ')}
            </span>
          )}
        </>
      ),
    },
    { clave: 'unidad', titulo: t('precios.unidad'), contenido: (fila) => fila.unidad },
    {
      clave: 'precio',
      titulo: t('precios.precio'),
      numerica: true,
      contenido: (fila) =>
        puedeEditar ? (
          <input
            aria-label={`${t('precios.precio')} ${fila.codigo}`}
            className="numero w-32 rounded-marco border border-borde bg-superficie px-2 py-1 text-right text-sm"
            inputMode="decimal"
            value={
              editados[fila.varianteId] ?? (fila.precio === null ? '' : paraEditar(fila.precio))
            }
            onChange={(evento) => {
              setEditados((previo) => ({ ...previo, [fila.varianteId]: evento.target.value }));
            }}
          />
        ) : (
          <span className="numero">
            {fila.precio === null ? t('precios.sinPrecio') : paraEditar(fila.precio)}
          </span>
        ),
    },
    {
      clave: 'costo',
      titulo: t('precios.costoDeReposicion'),
      numerica: true,
      // Con su moneda y su fecha. **Un costo sin fecha no se puede juzgar**, y uno sin moneda es
      // ambiguo justo en el caso que importa: el proveedor que cotiza en dólares.
      contenido: (fila) => (
        <button
          type="button"
          className="numero text-right text-sm underline decoration-dotted underline-offset-2"
          disabled={!puedeEditar}
          onClick={() => {
            setCostoDe(fila);
            setCostoEditado(
              fila.costoDeReposicion === null ? '' : paraEditar(fila.costoDeReposicion),
            );
            setMonedaDelCosto(
              fila.monedaDelCosto ??
                monedas.data?.find((moneda) => moneda.esBase)?.monedaCodigo ??
                '',
            );
          }}
        >
          {fila.costoDeReposicion === null ? (
            <span className="text-texto-tenue">{t('precios.sinCosto')}</span>
          ) : (
            <>
              {`${fila.monedaDelCosto ?? ''} ${paraEditar(fila.costoDeReposicion)}`}
              {fila.costoVigenteDesde === null ? null : (
                <span className="block text-apoyo text-texto-tenue">
                  {fila.costoVigenteDesde.slice(0, 10)}
                </span>
              )}
            </>
          )}
        </button>
      ),
    },
    {
      clave: 'historial',
      titulo: t('precios.historial'),
      contenido: (fila) => (
        <div className="flex justify-end gap-1">
          {fila.precio === null ? null : (
            <Boton
              tono="sutil"
              onClick={() => {
                setHistorialDe(fila);
              }}
            >
              {t('precios.historial')}
            </Boton>
          )}
          {puedeEditar ? (
            <Boton
              tono="sutil"
              onClick={() => {
                setProgramando(fila);
                setPrograma({ precio: '', fecha: '' });
                setErrorDeProgramar(null);
              }}
            >
              {t('precios.programar')}
            </Boton>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('precios.titulo')}
        descripcion={t('precios.descripcion')}
        acciones={
          puedeAumentar ? (
            <Boton
              onClick={() => {
                setAumentoAbierto(true);
              }}
            >
              {t('precios.aumentar')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Selector
              etiqueta={t('precios.lista')}
              value={listaActivaId}
              onChange={(evento) => {
                setListaId(evento.target.value);
                setEditados({});
                setAviso(null);
              }}
            >
              {(listas.data ?? []).map((lista) => (
                <option key={lista.id} value={lista.id}>
                  {lista.nombre}
                </option>
              ))}
            </Selector>
          </div>

          <div className="max-w-sm flex-1">
            <Campo
              etiqueta={t('precios.buscar')}
              type="search"
              value={busqueda}
              onChange={(evento) => {
                setBusqueda(evento.target.value);
              }}
            />
          </div>

          {/* En qué lado del impuesto está escrita la lista. Es lo que evita cargar el
              número equivocado, y por eso está a la vista y no escondido en la ficha. */}
          {listaActiva === undefined ? null : (
            <Insignia tono="neutro">
              {listaActiva.expresion === 'neto'
                ? t('precios.expresionNeto')
                : t('precios.expresionFinal')}
            </Insignia>
          )}

          {sinPrecio === 0 ? null : (
            <Insignia tono="atencion">{t('precios.faltan', { count: sinPrecio })}</Insignia>
          )}
        </div>

        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.varianteId}
            vacio={<Vacio descripcion={t('precios.vacio')} />}
          />
        ) : null}

        {puedeEditar ? (
          <div className="mt-3 flex items-center gap-3">
            <Boton
              tono="principal"
              cargando={guardar.isPending}
              disabled={cambios.length === 0}
              onClick={() => {
                guardar.mutate();
              }}
            >
              {cambios.length === 0
                ? t('precios.sinCambios')
                : t('precios.guardar', { count: cambios.length })}
            </Boton>
            {aviso === null ? null : (
              <p role="status" className="text-xs text-bien">
                {aviso}
              </p>
            )}
            {error === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {error}
              </p>
            )}
          </div>
        ) : null}
      </Tarjeta>

      {/* Lo que espera su fecha, en esta lista. Se ve y se cancela mientras espera (E36). */}
      {(programados.data ?? []).length === 0 ? null : (
        <Tarjeta titulo={t('precios.programados')} descripcion={t('precios.ayudaProgramados')}>
          <Tabla
            columnas={[
              {
                clave: 'fecha',
                titulo: t('precios.desdeElDia'),
                contenido: (fila) => (
                  <span className="numero">{fechaParaMirar(fila.vigenteDesde, i18n.language)}</span>
                ),
              },
              {
                clave: 'articulo',
                titulo: t('precios.articulo'),
                contenido: (fila) => (
                  <>
                    <span className="block">{fila.nombre}</span>
                    <span className="block text-xs text-texto-tenue">{fila.codigo}</span>
                  </>
                ),
              },
              {
                clave: 'hoy',
                titulo: t('precios.precioDeHoy'),
                numerica: true,
                contenido: (fila) =>
                  fila.precioDeHoy === null ? '—' : paraEditar(fila.precioDeHoy),
              },
              {
                clave: 'nuevo',
                titulo: t('precios.precioProgramado'),
                numerica: true,
                contenido: (fila) => paraEditar(fila.precio),
              },
              {
                clave: 'acciones',
                titulo: '',
                contenido: (fila) =>
                  !puedeEditar ? null : (
                    <Boton
                      tono="sutil"
                      onClick={() => {
                        cancelarProgramado.mutate(fila.id);
                      }}
                    >
                      {t('acciones.cancelar')}
                    </Boton>
                  ),
              },
            ]}
            filas={programados.data ?? []}
            claveDeFila={(fila) => fila.id}
          />
        </Tarjeta>
      )}

      <Dialogo
        abierto={programando !== null}
        titulo={t('precios.programarPara', { articulo: programando?.nombre ?? '' })}
        onCerrar={() => {
          setProgramando(null);
          setErrorDeProgramar(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={programar.isPending}
            disabled={programa.precio.trim() === '' || programa.fecha === ''}
            onClick={() => {
              programar.mutate();
            }}
          >
            {t('precios.programar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-xs text-texto-tenue">{t('precios.ayudaProgramar')}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo
              etiqueta={t('precios.precioProgramado')}
              obligatorio
              inputMode="decimal"
              value={programa.precio}
              onChange={(evento) => {
                setPrograma((previo) => ({ ...previo, precio: evento.target.value }));
              }}
            />
            <Campo
              etiqueta={t('precios.desdeElDia')}
              obligatorio
              type="date"
              value={programa.fecha}
              onChange={(evento) => {
                setPrograma((previo) => ({ ...previo, fecha: evento.target.value }));
              }}
            />
          </div>
          {errorDeProgramar === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {errorDeProgramar}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={aumentoAbierto}
        titulo={t('precios.aumentar')}
        descripcion={t('precios.aplicaA', { count: conPrecio })}
        onCerrar={() => {
          setAumentoAbierto(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={aumentar.isPending}
            disabled={normalizar(porcentaje) === ''}
            onClick={() => {
              aumentar.mutate();
            }}
          >
            {t('acciones.confirmar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('precios.porcentaje')}
            obligatorio
            inputMode="decimal"
            value={porcentaje}
            ayuda={t('precios.ayudaPorcentaje')}
            onChange={(evento) => {
              setPorcentaje(evento.target.value);
            }}
          />
          <Selector
            etiqueta={t('precios.redondeo')}
            value={decimales}
            ayuda={t('precios.ayudaRedondeo')}
            onChange={(evento) => {
              setDecimales(evento.target.value);
            }}
          >
            <option value="0">{t('precios.redondeoPeso')}</option>
            <option value="2">{t('precios.redondeoCentavo')}</option>
            <option value="4">{t('precios.redondeoCuatro')}</option>
          </Selector>
          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={historialDe !== null}
        titulo={t('precios.historialDe', { articulo: historialDe?.nombre ?? '' })}
        onCerrar={() => {
          setHistorialDe(null);
        }}
      >
        {historial.isPending ? <Cargando /> : null}
        {historial.isSuccess ? (
          <Tabla
            columnas={[
              {
                clave: 'desde',
                titulo: t('precios.desde'),
                contenido: (linea: LineaDeHistorial) => linea.vigenteDesde.slice(0, 10),
              },
              {
                clave: 'anterior',
                titulo: t('precios.anterior'),
                numerica: true,
                contenido: (linea: LineaDeHistorial) => (
                  <span className="numero">
                    {linea.precioAnterior === null ? '—' : paraEditar(linea.precioAnterior)}
                  </span>
                ),
              },
              {
                clave: 'precio',
                titulo: t('precios.precio'),
                numerica: true,
                contenido: (linea: LineaDeHistorial) => (
                  <span className="numero">{paraEditar(linea.precio)}</span>
                ),
              },
              {
                clave: 'motivo',
                titulo: t('precios.motivo'),
                contenido: (linea: LineaDeHistorial) => t(claveDeMotivo(linea.motivo)),
              },
              {
                clave: 'quien',
                titulo: t('precios.quien'),
                contenido: (linea: LineaDeHistorial) => linea.correoDelUsuario ?? '—',
              },
            ]}
            filas={historial.data}
            claveDeFila={(linea) => linea.id}
            vacio={<Vacio descripcion={t('precios.sinHistorial')} />}
          />
        ) : null}
      </Dialogo>

      {/*
        El costo de reposición **se reemplaza, no se historia**: es una estimación de hoy, no un
        hecho. No hay «a cuánto lo reponía en marzo» que contestar. Lo que sí queda es desde cuándo
        vale, que es lo que permite mirar un costo y saber si está viejo.
      */}
      <Dialogo
        abierto={costoDe !== null}
        titulo={t('precios.cargarCosto')}
        descripcion={costoDe?.nombre ?? ''}
        onCerrar={() => {
          setCostoDe(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={guardarCosto.isPending}
            disabled={normalizar(costoEditado) === '' || monedaDelCosto === ''}
            onClick={() => {
              guardarCosto.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex gap-3">
          <Campo
            etiqueta={t('precios.costoDeReposicion')}
            value={costoEditado}
            inputMode="decimal"
            ayuda={t('precios.ayudaCosto')}
            onChange={(evento) => {
              setCostoEditado(evento.target.value);
            }}
          />
          <Selector
            etiqueta={t('precios.moneda')}
            value={monedaDelCosto}
            onChange={(evento) => {
              setMonedaDelCosto(evento.target.value);
            }}
          >
            {(monedas.data ?? []).map((moneda) => (
              <option key={moneda.monedaCodigo} value={moneda.monedaCodigo}>
                {moneda.monedaCodigo}
              </option>
            ))}
          </Selector>
        </div>
      </Dialogo>
    </>
  );
}
